import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Globe, EyeOff } from "lucide-react";
import LogoUploader from "../../components/admin/LogoUploader";

const TIERS = ["Community Supporter", "Bronze", "Silver", "Gold", "Platinum", "Diamond"];
const BLANK = {
  name: "", tier: "Gold", logo_url: "", website_url: "",
  description: "", published: false, featured: false, sort_order: 0,
};

export default function SponsorsPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/sponsors", { withCredentials: true });
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.id; delete body.created_at; delete body.updated_at;
      if (editing.id) {
        await api.patch(`/admin/sponsors/${editing.id}`, body, { withCredentials: true });
        toast.success("Sponsor updated");
      } else {
        await api.post("/admin/sponsors", body, { withCredentials: true });
        toast.success("Sponsor added");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sponsor?")) return;
    await api.delete(`/admin/sponsors/${id}`, { withCredentials: true });
    toast.success("Deleted");
    await load();
  };

  const togglePublish = async (s) => {
    await api.patch(`/admin/sponsors/${s.id}`,
      { ...s, published: !s.published, id: undefined, created_at: undefined, updated_at: undefined },
      { withCredentials: true });
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">SPONSORS</p>
          <h1 className="font-display text-4xl text-white">Featured Sponsors</h1>
          <p className="text-white/50 mt-2 text-sm">Published sponsors appear in the homepage carousel.</p>
        </div>
        {editable && (
          <button data-testid="add-sponsor-btn" onClick={() => setEditing({ ...BLANK })} className="btn-pink">
            <Plus size={16} /> ADD SPONSOR
          </button>
        )}
      </div>

      <div className="border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-white/40">No sponsors yet. Add one to feature on the homepage.</td></tr>
            )}
            {items.map((s) => (
              <tr key={s.id} data-testid={`sponsor-row-${s.id}`} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white">{s.name}</td>
                <td className="px-4 py-3 text-white/70">{s.tier || "—"}</td>
                <td className="px-4 py-3">{s.featured ? <span className="text-aawta-pink">★</span> : <span className="text-white/30">—</span>}</td>
                <td className="px-4 py-3">
                  {editable ? (
                    <button onClick={() => togglePublish(s)} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${s.published ? "border-aawta-pink/50 text-aawta-pink" : "border-white/20 text-white/50"}`}>
                      {s.published ? <Globe size={12} /> : <EyeOff size={12} />}
                      {s.published ? "PUBLISHED" : "DRAFT"}
                    </button>
                  ) : (
                    <span className="text-xs text-white/50">{s.published ? "Published" : "Draft"}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editable && (
                    <>
                      <button onClick={() => setEditing(s)} className="text-white/60 hover:text-aawta-pink mr-3"><Edit3 size={14} /></button>
                      <button onClick={() => remove(s.id)} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Sponsor" : "Add Sponsor"} onClose={() => setEditing(null)}>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="sponsor-name" />
            <SelectField label="Tier" value={editing.tier} onChange={(v) => setEditing({ ...editing, tier: v })} options={TIERS} testId="sponsor-tier" />
            <div className="md:col-span-2">
              <LogoUploader label="Logo" value={editing.logo_url} onChange={(v) => setEditing({ ...editing, logo_url: v })} testId="sponsor-logo" />
            </div>
            <TextField label="Website URL" value={editing.website_url} onChange={(v) => setEditing({ ...editing, website_url: v })} placeholder="https://…" testId="sponsor-website" />
            <div className="md:col-span-2">
              <TextArea label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testId="sponsor-desc" />
            </div>
            <NumField label="Sort Order" value={editing.sort_order} onChange={(v) => setEditing({ ...editing, sort_order: v })} testId="sponsor-sort" />
            <div className="flex items-end gap-6">
              <Toggle label="Published" value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testId="sponsor-publish" />
              <Toggle label="Featured (carousel)" value={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} testId="sponsor-feature" />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="btn-ghost py-2.5">CANCEL</button>
            <button data-testid="save-sponsor-btn" onClick={save} disabled={saving} className="btn-pink disabled:opacity-50">
              {saving ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-aawta-charcoal border border-white/15 max-w-3xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-display text-2xl text-white">{title}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function TextField({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <input data-testid={testId} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm transition-colors" />
    </label>
  );
}
export function TextArea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <textarea data-testid={testId} value={value || ""} rows={3} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm transition-colors resize-none" />
    </label>
  );
}
export function NumField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <input data-testid={testId} type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm font-mono transition-colors" />
    </label>
  );
}
export function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm transition-colors">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
export function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <span className="relative inline-block w-10 h-5">
        <input data-testid={testId} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <span className="absolute inset-0 bg-white/10 peer-checked:bg-aawta-pink transition-colors rounded-full" />
        <span className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
      </span>
      <span className="text-sm text-white/80">{label}</span>
    </label>
  );
}
